import React, { useEffect, useState } from 'react';
import { CalendarClock, Check, Loader2, RotateCcw, Save, XCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { tradeLabel } from '@/data/constants';
import { locationLabel } from '@/lib/listingSettings';
import {
  DEFAULT_EXPIRY,
  ExpiryRow,
  ExpirySettings,
  VERDICT_COPY,
  classifyListing,
  clearConfirmation,
  closeListing,
  confirmStillActive,
  daysPastTarget,
  loadExpiryQueue,
  loadExpirySettings,
  saveExpirySettings,
} from '@/lib/listingExpiry';

/**
 * ADMIN → Settings → "Past target dates".
 *
 * Audit follow-up #1. A published project whose target date has passed must
 * not live on the public marketplace forever, but it must never be deleted.
 * This queue is the human review step: confirm it is still accepting
 * contractors (returns it to the feed), or close it as completed / cancelled
 * (leaves the feed, row retained for the owner, admins and past purchasers).
 *
 * The grace window itself is configurable here and is read by the
 * mkt_listings_public view, so no deploy is needed to change it.
 */
const AdminExpiryQueue: React.FC = () => {
  const { lang } = useI18n();
  const { profile } = useAuth();
  const es = lang === 'es';

  const [settings, setSettings] = useState<ExpirySettings>(DEFAULT_EXPIRY);
  const [rows, setRows] = useState<ExpiryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const refresh = async () => {
    const [s, q] = await Promise.all([loadExpirySettings(), loadExpiryQueue()]);
    setSettings(s);
    setRows(q);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setMsg('');
    await fn();
    await refresh();
    setBusyId('');
  };

  const persist = async () => {
    setSaving(true);
    setMsg('');
    const error = await saveExpirySettings(settings);
    setSaving(false);
    setMsg(error ? (es ? 'No se pudo guardar' : 'Could not save') : es ? 'Guardado' : 'Saved');
    if (!error) refresh();
  };

  const hidden = rows.filter((r) => classifyListing(r, settings) === 'expired_hidden').length;
  const pending = rows.filter((r) => classifyListing(r, settings) === 'needs_confirmation').length;

  const btn =
    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-colors disabled:opacity-50';

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
        <CalendarClock className="h-5 w-5 text-orange-500" aria-hidden="true" />
        {es ? 'Fechas de proyecto vencidas' : 'Past target dates'}
      </h2>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        {es
          ? 'Proyectos publicados cuya fecha objetivo ya pasó. Nada se elimina: al vencer el periodo de gracia solo dejan de mostrarse en el marketplace público. El dueño, los administradores y cualquier contratista que ya compró el lead conservan el acceso.'
          : 'Published projects whose target date has already passed. Nothing is deleted — once the grace window elapses they simply stop appearing on the public marketplace. The homeowner, administrators and any contractor who already purchased the lead keep full access.'}
      </p>

      {/* ---- configurable window ------------------------------------------ */}
      <div className="mt-5 grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-3">
        <div>
          <label htmlFor="exp-enabled" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Regla activa' : 'Rule enabled'}
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <input
              id="exp-enabled"
              type="checkbox"
              className="h-4 w-4"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            />
            {settings.enabled ? (es ? 'Ocultar vencidos' : 'Hide expired') : es ? 'Mostrar todos' : 'Show all'}
          </label>
        </div>
        <div>
          <label htmlFor="exp-grace" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Días de gracia' : 'Grace days'}
          </label>
          <input
            id="exp-grace"
            type="number"
            min={0}
            max={365}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-900 focus:border-slate-900 focus:outline-none"
            value={settings.grace_days}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setSettings({ ...settings, grace_days: Number.isFinite(n) && n >= 0 ? Math.min(n, 365) : 0 });
            }}
          />
        </div>
        <div className="flex items-end">
          <button onClick={persist} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {es ? 'Guardar' : 'Save'}
          </button>
        </div>
      </div>

      <p className="mt-4 text-sm font-semibold text-slate-700" role="status" aria-live="polite">
        {loading
          ? es
            ? 'Cargando…'
            : 'Loading…'
          : es
            ? `${rows.length} proyectos vencidos · ${pending} por confirmar · ${hidden} ocultos`
            : `${rows.length} past-dated projects · ${pending} awaiting confirmation · ${hidden} hidden`}
      </p>
      {msg && <p className="mt-2 text-sm font-semibold text-emerald-700">{msg}</p>}

      {!loading && rows.length === 0 && (
        <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          {es ? 'Ningún proyecto publicado tiene la fecha vencida.' : 'No published project is past its target date.'}
        </p>
      )}

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{es ? 'Proyecto' : 'Project'}</th>
                <th className="px-4 py-3">{es ? 'Ubicación' : 'Location'}</th>
                <th className="px-4 py-3">{es ? 'Fecha' : 'Target'}</th>
                <th className="px-4 py-3">{es ? 'Estado' : 'Verdict'}</th>
                <th className="px-4 py-3 text-right">{es ? 'Acción' : 'Action'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const verdict = classifyListing(r, settings);
                const copy = VERDICT_COPY[verdict];
                const busy = busyId === r.id;
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{r.title}</p>
                      <p className="text-xs text-slate-500">
                        {tradeLabel(r.trade, lang)} · {r.id.slice(0, 8)}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{locationLabel({ city: r.city, state: r.state })}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {String(r.target_date || '').slice(0, 10)}
                      <span className="block text-xs text-slate-400">
                        {daysPastTarget(r.target_date)} {es ? 'días' : 'days'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${copy.tone}`}>
                        {es ? copy.es : copy.en}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {r.active_confirmed_at ? (
                          <button
                            disabled={busy}
                            onClick={() => act(r.id, () => clearConfirmation(r.id))}
                            className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                            {es ? 'Quitar confirmación' : 'Un-confirm'}
                          </button>
                        ) : (
                          <button
                            disabled={busy || !profile?.id}
                            onClick={() => act(r.id, () => confirmStillActive(r.id, profile!.id))}
                            className={`${btn} border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100`}
                          >
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            {es ? 'Sigue activo' : 'Still active'}
                          </button>
                        )}
                        <button
                          disabled={busy}
                          onClick={() => act(r.id, () => closeListing(r.id, 'completed'))}
                          className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}
                        >
                          {es ? 'Completado' : 'Completed'}
                        </button>
                        <button
                          disabled={busy}
                          onClick={() => act(r.id, () => closeListing(r.id, 'cancelled'))}
                          className={`${btn} border-red-300 text-red-700 hover:bg-red-50`}
                        >
                          <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                          {es ? 'Cancelado' : 'Cancelled'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminExpiryQueue;
